import { useMemo, useState } from "react";
import { message } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Send, Sparkles } from "lucide-react";

import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useTenantStore } from "@/stores/useTenantStore";
import { useWorkspaceStore } from "@/stores/useWorkspaceStore";
import {
  supportService,
  type SupportCategory,
  type SupportEntryPoint,
} from "@/services/supportService";
import { TICKETS_QUERY_KEY } from "@/hooks/useTickets";
import { useTicketAIDraft } from "@/hooks/useTicketAIDraft";
import { useSupportCategories } from "@/hooks/useSupportCategories";
import type { TicketDraftMessage, TicketPriority } from "@/services/ticketsService";
import { CATEGORY_OPTION_TEXT, resolveOfferedCategory } from "./ticketConstants";

interface NewTicketModalProps {
  open: boolean;
  onClose: () => void;
  /** Starting value for the review form's category — still just a seed,
   *  not a lock: an AI draft that infers a different category still wins,
   *  same as if the user picked one manually and then changed their mind. */
  initialCategory?: SupportCategory;
  /** Which UI opened this modal — provenance only, the AI never sees or
   *  sets this. Defaults to the /support page's own entry point. */
  entryPoint?: SupportEntryPoint;
}

const PRIORITIES: { value: TicketPriority; label: string }[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

export function NewTicketModal({
  open,
  onClose,
  initialCategory,
  entryPoint = "support_page",
}: NewTicketModalProps) {
  const { currentTenant } = useTenantStore();
  const queryClient = useQueryClient();
  // Which client the ticket is ABOUT — not whichever one is selected in the
  // header. Two real prod tickets were filed under the wrong client because
  // the active workspace was the only thing asked (2026-09-09).
  const allWorkspaces = useWorkspaceStore((s) => s.workspaces);
  const workspaces = useMemo(
    () => allWorkspaces.filter((w) => Boolean(w.website)),
    [allWorkspaces],
  );
  const showClientPicker = workspaces.length > 1;
  const [aboutClient, setAboutClient] = useState<string | undefined>(
    currentTenant || undefined,
  );

  const [step, setStep] = useState<"chat" | "review">("chat");
  const [messages, setMessages] = useState<TicketDraftMessage[]>([]);
  const [chatInput, setChatInput] = useState("");

  const [category, setCategory] = useState<SupportCategory | "">(initialCategory ?? "");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<TicketPriority>("medium");
  const [handoffMessage, setHandoffMessage] = useState<string | null>(null);

  const draft = useTicketAIDraft();

  // Which categories may be offered depends on the backend routing flag.
  const { routingEnabled, categories } = useSupportCategories(open);
  const options = categories.map((c) => ({
    value: c.key,
    label: CATEGORY_OPTION_TEXT[c.key]?.label ?? c.label,
    hint: CATEGORY_OPTION_TEXT[c.key]?.hint,
  }));
  // `category` holds what the user or the AI draft proposed; the effective
  // value is resolved against what is on offer at render time, so it is right
  // whichever arrives first (the draft or the categories).
  const effectiveCategory = resolveOfferedCategory(
    category,
    options.map((o) => o.value),
    routingEnabled,
  );

  const resetAll = () => {
    setStep("chat");
    setMessages([]);
    setChatInput("");
    setCategory(initialCategory ?? "");
    setTitle("");
    setDescription("");
    setPriority("medium");
    setHandoffMessage(null);
    setAboutClient(currentTenant || undefined);
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const submit = useMutation({
    mutationFn: () =>
      supportService.submit({
        category: effectiveCategory as SupportCategory,
        message: description,
        workspace: aboutClient || currentTenant || undefined,
        title: title.trim() || undefined,
        priority,
        entryPoint,
      }),
    onSuccess: (res) => {
      if (res.ok === false) {
        // ok:true used to come back even when the ticket was never saved.
        message.error(
          "We couldn't record your request. Please try again or contact the team directly.",
        );
        return;
      }
      queryClient.invalidateQueries({ queryKey: TICKETS_QUERY_KEY });
      message.success(`Sent to our ${res.team} — we'll be in touch.`);
      handleClose();
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Couldn't send your request. Please try again.";
      message.error(detail);
    },
  });

  const sendChatMessage = () => {
    const text = chatInput.trim();
    if (!text || draft.isPending) return;
    const nextMessages: TicketDraftMessage[] = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setChatInput("");
    draft.mutate(nextMessages, {
      onSuccess: (result) => {
        if (result.type === "question") {
          setMessages((prev) => [...prev, { role: "assistant", content: result.question }]);
        } else if (result.type === "manual") {
          // Terminal hand-off (LLM couldn't produce a confident draft after
          // already asking once) — jump to the review form instead of
          // showing another chat bubble, which would have nowhere to go.
          // Surface why, so the user isn't silently dropped into a form.
          setHandoffMessage(result.message);
          setDescription((prev) => prev || result.prefill_description);
          setStep("review");
        } else {
          // Never auto-submit — seed the review form, user must confirm.
          setHandoffMessage(null);
          setTitle(result.title);
          setDescription(result.description);
          setCategory(result.category);
          setPriority(result.priority);
          setStep("review");
        }
      },
      onError: () => {
        message.error("Couldn't reach the assistant — you can still fill the request manually.");
      },
    });
  };

  const skipToManual = () => {
    setHandoffMessage(null);
    setStep("review");
  };

  const canSubmit =
    effectiveCategory !== "" && title.trim().length > 0 && description.trim().length > 0 && !submit.isPending;
  const activeHint = options.find((c) => c.value === effectiveCategory)?.hint;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{step === "chat" ? "New Request" : "Review request"}</DialogTitle>
        </DialogHeader>

        {step === "chat" ? (
          <>
            <div className="flex-1 space-y-3 overflow-y-auto rounded-md border border-border bg-muted/20 p-3">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted-foreground">
                  <Sparkles className="h-5 w-5 text-coral" />
                  <p>Tell me what's going on — I'll help turn it into a clear request.</p>
                </div>
              ) : (
                messages.map((m, i) => (
                  <div
                    key={i}
                    className={cn(
                      "max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed",
                      m.role === "user"
                        ? "ml-auto bg-coral text-white"
                        : "mr-auto border border-border bg-card text-foreground",
                    )}
                  >
                    {m.content}
                  </div>
                ))
              )}
              {draft.isPending ? (
                <div className="mr-auto flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
                </div>
              ) : null}
            </div>

            <div className="flex items-end gap-2 pt-1">
              <Textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendChatMessage();
                  }
                }}
                placeholder="What's going on? Include the page/URL or any details that help."
                rows={2}
                className="flex-1"
              />
              <Button
                type="button"
                size="icon"
                onClick={sendChatMessage}
                disabled={!chatInput.trim() || draft.isPending}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>

            <button
              type="button"
              onClick={skipToManual}
              className="self-start text-xs font-medium text-muted-foreground hover:text-foreground hover:underline"
            >
              Skip — fill manually
            </button>
          </>
        ) : (
          <>
            {messages.length > 0 ? (
              <button
                type="button"
                onClick={() => setStep("chat")}
                className="flex items-center gap-1 self-start text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to chat
              </button>
            ) : null}

            {handoffMessage ? (
              <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                {handoffMessage}
              </p>
            ) : null}

            <div className="space-y-4 overflow-y-auto">
              <div className="space-y-2">
                <Label htmlFor="new-ticket-category">What do you need help with?</Label>
                <Select
                  value={effectiveCategory || undefined}
                  onValueChange={(v) => setCategory(v as SupportCategory)}
                >
                  <SelectTrigger id="new-ticket-category">
                    <SelectValue placeholder="Choose a category…" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {activeHint && <p className="text-sm text-muted-foreground">{activeHint}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-ticket-title">Title</Label>
                <Input
                  id="new-ticket-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Short summary"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-ticket-description">Describe your request</Label>
                <Textarea
                  id="new-ticket-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What's going on? Include the page/URL or any details that help."
                  rows={5}
                />
              </div>

              {showClientPicker && (
                <div className="space-y-2">
                  <Label htmlFor="new-ticket-client">
                    Which client is this about?
                  </Label>
                  <Select value={aboutClient} onValueChange={setAboutClient}>
                    <SelectTrigger id="new-ticket-client">
                      <SelectValue placeholder="Choose a client…" />
                    </SelectTrigger>
                    <SelectContent>
                      {workspaces.map((w) => (
                        <SelectItem key={w.id} value={w.website as string}>
                          {w.name || w.website}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    Defaults to the client you have open.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="new-ticket-priority">Priority</Label>
                <Select value={priority} onValueChange={(v) => setPriority(v as TicketPriority)}>
                  <SelectTrigger id="new-ticket-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={() => submit.mutate()} disabled={!canSubmit}>
                {submit.isPending ? "Creating…" : "Create ticket"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
